import {Button, Drawer, FormInstance, Modal, Space, Spin} from 'antd';
import ProForm, {ProFormProps} from '../../../ProForm';
import {DrawerProps} from 'antd/es';
import React, {useState} from 'react';
import useRequestFn from '../../../../Utils/Hooks/useRequestFn';
import {useForm} from 'antd/es/form/Form';
import {ButtonProps} from 'antd/lib/button/button';

type FormProps<T> = Omit<
    ProFormProps<T>,
    'className' | 'form' | 'onFinish' | 'initialValues' | 'disabled'
>;

export interface FormDrawerProps<T> extends DrawerProps {
    onFinish: (value: T) => Promise<any>;
    initialValues?: Partial<T>;
    formProps?: FormProps<T>;
    form?: FormInstance<T>;
    loading?: boolean;
    okButtonProps?: ButtonProps;
    cancelButtonProps?: ButtonProps;
}

export function FormDrawer<T>({
    children,
    onFinish,
    initialValues,
    formProps,
    form,
    loading,
    okButtonProps,
    cancelButtonProps,
    ...props
}: FormDrawerProps<T>) {
    const {onValuesChange, ...extraFormProps} = formProps || {
        columns: 1,
        wrapperCol: {offset: 1},
    };

    const [touched, setTouched] = useState(false);
    const [callOnFinish, submitState] = useRequestFn(onFinish);
    const [innerForm] = useForm<T>(form);

    async function onSubmit(data: T) {
        await callOnFinish(data);
    }

    function onClose(e: React.MouseEvent | React.KeyboardEvent) {
        if (touched) {
            Modal.confirm({
                content: '信息未保存，确定关闭页面吗？',
                onOk: () => props?.onClose?.(e),
            });
        } else {
            props?.onClose?.(e);
        }
    }

    // 表单变更时修改touched树形
    function handleValuesChange(changedValues: any, values: T) {
        setTouched(innerForm.isFieldsTouched());
        onValuesChange?.(changedValues, values);
    }

    const footer = (
        <Space style={{display: 'flex', justifyContent: 'flex-end'}}>
            <Button onClick={onClose} {...okButtonProps}>
                取消
            </Button>

            <Button
                type="primary"
                disabled={loading || !touched}
                loading={submitState.loading}
                onClick={innerForm.submit}
                {...cancelButtonProps}>
                确定
            </Button>
        </Space>
    );

    return (
        <Drawer footer={footer} onClose={onClose} {...props}>
            <Spin spinning={loading}>
                <ProForm
                    form={innerForm}
                    initialValues={initialValues}
                    onFinish={onSubmit}
                    onValuesChange={handleValuesChange}
                    {...extraFormProps}>
                    {children}
                </ProForm>
            </Spin>
        </Drawer>
    );
}

FormDrawer.defaultProps = {
    maskClosable: false,
    loading: false,
};
